import { useState, useEffect } from 'react';
import clientAdminService from '../services/clientAdminService';
import EditableSelect from '../components/cashReserves/EditableSelect';
import { formatDate } from '../utils/dateUtils';

const EMPTY_FORM = { client_name: '', description: '', nicknames: [], phone_number: '' };

const normalizeName = (name) => (name || '').trim().toLowerCase();

const ClientAdmin = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);

    // Synced directory households for the create form's name picker. null
    // means "not loaded yet"; loaded lazily the first time the modal opens.
    const [households, setHouseholds] = useState(null);
    const [householdsLoading, setHouseholdsLoading] = useState(false);
    const [householdsError, setHouseholdsError] = useState('');
    
    // Search and sorting state
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    // Fetch clients on mount
    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            setError('');
            const clientData = await clientAdminService.fetchAllClients();
            setClients(clientData);
        } catch (err) {
            setError(`Failed to fetch clients: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadHouseholds = async () => {
        try {
            setHouseholdsLoading(true);
            setHouseholdsError('');
            setHouseholds(await clientAdminService.fetchDirectoryHouseholds());
        } catch (err) {
            // Non-blocking: the name field still takes free text.
            setHouseholdsError(err.response?.data?.detail || err.message);
        } finally {
            setHouseholdsLoading(false);
        }
    };

    // Households already linked to a profile aren't offered again. Several
    // households can share a name, so options are one per name and a link is
    // only made when the typed name matches exactly one household.
    const unlinkedHouseholds = (households || []).filter((h) => !h.linked_client_id);
    const householdsByName = unlinkedHouseholds.reduce((byName, household) => {
        const key = normalizeName(household.household_name);
        byName.set(key, [...(byName.get(key) || []), household]);
        return byName;
    }, new Map());
    const householdOptions = [...householdsByName.values()].map((matches) => ({
        value: matches[0].household_name,
        label: matches.length === 1
            ? matches[0].contact_names.join(', ')
            : `${matches.length} directory households`,
    }));
    const nameMatches = householdsByName.get(normalizeName(formData.client_name)) || [];
    const matchedHousehold = nameMatches.length === 1 && nameMatches[0].account_id
        ? nameMatches[0]
        : null;

    const handleCreateClient = async (e) => {
        e.preventDefault();
        if (!formData.client_name.trim()) {
            setError('Client name is required');
            return;
        }

        // Validate phone number if provided
        if (formData.phone_number.trim()) {
            const phone = formData.phone_number.trim();
            if (phone.length !== 12 || !phone.startsWith('+1')) {
                setError('Phone number must be exactly 12 characters and start with +1 (e.g., +12025551234)');
                return;
            }
        }

        try {
            setSubmitting(true);
            setError('');
            const created = await clientAdminService.createClient({
                client_name: formData.client_name.trim(),
                description: formData.description.trim() || undefined,
                nicknames: formData.nicknames.filter(nickname => nickname.trim() !== ''),
                phone_number: formData.phone_number.trim() || undefined,
                tamarac_account_id: matchedHousehold?.account_id || undefined
            });
            if (matchedHousehold) {
                setHouseholds((prev) => prev.map((h) => (
                    h.account_id === matchedHousehold.account_id
                        ? { ...h, linked_client_id: created.client_id }
                        : h
                )));
            }
            setShowCreateModal(false);
            setFormData(EMPTY_FORM);
            await fetchClients();
        } catch (err) {
            setError(`Failed to create client: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditClient = async (e) => {
        e.preventDefault();
        if (!formData.client_name.trim()) {
            setError('Client name is required');
            return;
        }

        // Validate phone number if provided
        if (formData.phone_number.trim()) {
            const phone = formData.phone_number.trim();
            if (phone.length !== 12 || !phone.startsWith('+1')) {
                setError('Phone number must be exactly 12 characters and start with +1 (e.g., +12025551234)');
                return;
            }
        }

        try {
            setSubmitting(true);
            setError('');
            await clientAdminService.updateClient(selectedClient.client_id, {
                client_name: formData.client_name.trim(),
                description: formData.description.trim() || undefined,
                nicknames: formData.nicknames.filter(nickname => nickname.trim() !== ''),
                phone_number: formData.phone_number.trim() || undefined
            });
            setShowEditModal(false);
            setSelectedClient(null);
            setFormData(EMPTY_FORM);
            await fetchClients();
        } catch (err) {
            setError(`Failed to update client: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClient = async () => {
        try {
            setSubmitting(true);
            setError('');
            await clientAdminService.deleteClient(selectedClient.client_id);
            setShowDeleteModal(false);
            setSelectedClient(null);
            await fetchClients();
        } catch (err) {
            setError(`Failed to delete client: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const openCreateModal = () => {
        setFormData(EMPTY_FORM);
        setError('');
        setShowCreateModal(true);
        if (households === null && !householdsLoading) {
            loadHouseholds();
        }
    };

    const openEditModal = (client) => {
        setSelectedClient(client);
        setFormData({
            client_name: client.client_name,
            description: client.description || '',
            nicknames: client.nicknames || [],
            phone_number: client.phone_number || ''
        });
        setError('');
        setShowEditModal(true);
    };

    const openDeleteModal = (client) => {
        setSelectedClient(client);
        setError('');
        setShowDeleteModal(true);
    };

    const closeModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        setSelectedClient(null);
        setFormData(EMPTY_FORM);
        setError('');
    };

    // Search and sorting functions
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
        }
        return sortConfig.direction === 'asc' 
            ? <i className="bi bi-arrow-up text-primary ms-1"></i>
            : <i className="bi bi-arrow-down text-primary ms-1"></i>;
    };

    // Filter and sort clients
    const getFilteredAndSortedClients = () => {
        let filteredClients = clients;

        // Apply search filter
        if (searchTerm) {
            filteredClients = clients.filter(client =>
                client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                client.client_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (client.description && client.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (client.nicknames && client.nicknames.some(nickname => 
                    nickname.toLowerCase().includes(searchTerm.toLowerCase())
                ))
            );
        }

        // Apply sorting
        if (sortConfig.key) {
            filteredClients.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle null/undefined values
                if (!aValue) aValue = '';
                if (!bValue) bValue = '';

                // Convert to string for comparison
                aValue = aValue.toString().toLowerCase();
                bValue = bValue.toString().toLowerCase();

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredClients;
    };

    const filteredAndSortedClients = getFilteredAndSortedClients();

    // Nickname management functions
    const addNickname = () => {
        setFormData({ ...formData, nicknames: [...formData.nicknames, ''] });
    };

    const updateNickname = (index, value) => {
        const updatedNicknames = [...formData.nicknames];
        updatedNicknames[index] = value;
        setFormData({ ...formData, nicknames: updatedNicknames });
    };

    const removeNickname = (index) => {
        const updatedNicknames = formData.nicknames.filter((_, i) => i !== index);
        setFormData({ ...formData, nicknames: updatedNicknames });
    };

    // ...existing code...

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h1 className="h3 mb-0">
                            <i className="bi bi-shield-check me-2"></i>
                            Client Administration
                        </h1>
                        <button
                            className="btn btn-primary"
                            onClick={openCreateModal}
                            disabled={loading}
                        >
                            <i className="bi bi-plus-circle me-2"></i>
                            Add New Client
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setError('')}
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">
                                    <i className="bi bi-people me-2"></i>
                                    Clients ({filteredAndSortedClients.length}{searchTerm && ` of ${clients.length}`})
                                </h5>
                                <div className="d-flex align-items-center">
                                    <div className="input-group" style={{ width: '300px' }}>
                                        <span className="input-group-text">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by name, ID, description, or nicknames..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => setSearchTerm('')}
                                                title="Clear search"
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2">Loading clients...</p>
                                </div>
                            ) : filteredAndSortedClients.length === 0 ? (
                                <div className="text-center py-5">
                                    {searchTerm ? (
                                        <>
                                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                                            <p className="text-muted">
                                                No clients found matching "<strong>{searchTerm}</strong>"
                                            </p>
                                            <button
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                Clear search
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                                            <p className="text-muted">No clients found. Create your first client to get started!</p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleSort('client_name')}
                                                    title="Click to sort by Client Name"
                                                >
                                                    Client Name
                                                    {getSortIcon('client_name')}
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleSort('client_id')}
                                                    title="Click to sort by Client ID"
                                                >
                                                    Client ID
                                                    {getSortIcon('client_id')}
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleSort('description')}
                                                    title="Click to sort by Description"
                                                >
                                                    Description
                                                    {getSortIcon('description')}
                                                </th>
                                                <th>
                                                    Nicknames
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleSort('phone_number')}
                                                    title="Click to sort by Phone Number"
                                                >
                                                    Phone Number
                                                    {getSortIcon('phone_number')}
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleSort('created_at')}
                                                    title="Click to sort by Created Date"
                                                >
                                                    Created
                                                    {getSortIcon('created_at')}
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleSort('updated_at')}
                                                    title="Click to sort by Updated Date"
                                                >
                                                    Updated
                                                    {getSortIcon('updated_at')}
                                                </th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAndSortedClients.map((client) => (
                                                <tr key={client.client_id}>
                                                    <td>
                                                        <strong>{client.client_name}</strong>
                                                        {client.tamarac_account_id && (
                                                            <i
                                                                className="bi bi-link-45deg text-muted ms-1"
                                                                title={`Linked to directory household ${client.tamarac_account_id}`}
                                                                aria-label="Linked to directory household"
                                                            ></i>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <code className="text-primary">{client.client_id}</code>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            {client.description || 'No description'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {client.nicknames && client.nicknames.length > 0 ? (
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {client.nicknames.map((nickname, index) => (
                                                                    <span key={index} className="badge bg-secondary">
                                                                        {nickname}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">No nicknames</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {client.phone_number ? (
                                                            <span>
                                                                <i className="bi bi-phone me-1"></i>
                                                                {client.phone_number}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">No phone</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {formatDate(client.created_at)}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {formatDate(client.updated_at)}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group" role="group">
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => openEditModal(client)}
                                                                title="Edit Client"
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => openDeleteModal(client)}
                                                                title="Delete Client"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Client Modal */}
            {showCreateModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Create New Client
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeModals}
                                    disabled={submitting}
                                ></button>
                            </div>
                            <form onSubmit={handleCreateClient}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="createClientName" className="form-label">
                                            Client Name *
                                        </label>
                                        <EditableSelect
                                            id="createClientName"
                                            value={formData.client_name}
                                            onChange={(value) => setFormData((prev) => ({ ...prev, client_name: value }))}
                                            options={householdOptions}
                                            placeholder="Search the client directory or type a name"
                                            loading={householdsLoading}
                                            loadingMessage="Loading client directory…"
                                            emptyMessage={householdsError
                                                ? 'Client directory unavailable'
                                                : 'No new directory households available'}
                                            optionsLabel="Show directory households"
                                            disabled={submitting}
                                        />
                                        {matchedHousehold ? (
                                            <div className="form-text text-success">
                                                <i className="bi bi-link-45deg me-1"></i>
                                                Will be linked to directory household {matchedHousehold.household_name}.
                                            </div>
                                        ) : householdsError ? (
                                            <div className="form-text text-warning">
                                                Couldn't load the client directory ({householdsError}). You can still type a name.
                                            </div>
                                        ) : (
                                            <div className="form-text">
                                                Pick a household from the client directory to link it, or type a new name.
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="createClientDescription" className="form-label">
                                            Description
                                        </label>
                                        <textarea
                                            className="form-control"
                                            id="createClientDescription"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Enter optional description"
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="createClientPhone" className="form-label">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            id="createClientPhone"
                                            value={formData.phone_number}
                                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                            placeholder="+12025551234"
                                            pattern="\+1[0-9]{10}"
                                            maxLength="12"
                                            disabled={submitting}
                                        />
                                        <div className="form-text">
                                            Must be exactly 12 characters starting with +1 (e.g., +12025551234) for SMS notifications.
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Nicknames
                                        </label>
                                        <div className="mb-2">
                                            {formData.nicknames.map((nickname, index) => (
                                                <div key={index} className="input-group mb-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={nickname}
                                                        onChange={(e) => updateNickname(index, e.target.value)}
                                                        placeholder="Enter nickname"
                                                        disabled={submitting}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        onClick={() => removeNickname(index)}
                                                        disabled={submitting}
                                                        title="Remove nickname"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={addNickname}
                                                disabled={submitting}
                                            >
                                                <i className="bi bi-plus me-1"></i>
                                                Add Nickname
                                            </button>
                                        </div>
                                        <div className="form-text">
                                            Add alternative names or abbreviations for this client.
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModals}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={submitting || !formData.client_name.trim()}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-2"></i>
                                                Create Client
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Client Modal */}
            {showEditModal && selectedClient && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-pencil me-2"></i>
                                    Edit Client: {selectedClient.client_name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeModals}
                                    disabled={submitting}
                                ></button>
                            </div>
                            <form onSubmit={handleEditClient}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Client ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedClient.client_id}
                                            disabled
                                            readOnly
                                        />
                                        <div className="form-text">Client ID cannot be changed.</div>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editClientName" className="form-label">
                                            Client Name *
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editClientName"
                                            value={formData.client_name}
                                            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                                            placeholder="Enter client name"
                                            required
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editClientDescription" className="form-label">
                                            Description
                                        </label>
                                        <textarea
                                            className="form-control"
                                            id="editClientDescription"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Enter optional description"
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editClientPhone" className="form-label">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            id="editClientPhone"
                                            value={formData.phone_number}
                                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                            placeholder="+12025551234"
                                            pattern="\+1[0-9]{10}"
                                            maxLength="12"
                                            disabled={submitting}
                                        />
                                        <div className="form-text">
                                            Must be exactly 12 characters starting with +1 (e.g., +12025551234) for SMS notifications.
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">
                                            Nicknames
                                        </label>
                                        <div className="mb-2">
                                            {formData.nicknames.map((nickname, index) => (
                                                <div key={index} className="input-group mb-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={nickname}
                                                        onChange={(e) => updateNickname(index, e.target.value)}
                                                        placeholder="Enter nickname"
                                                        disabled={submitting}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        onClick={() => removeNickname(index)}
                                                        disabled={submitting}
                                                        title="Remove nickname"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={addNickname}
                                                disabled={submitting}
                                            >
                                                <i className="bi bi-plus me-1"></i>
                                                Add Nickname
                                            </button>
                                        </div>
                                        <div className="form-text">
                                            Add alternative names or abbreviations for this client.
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModals}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={submitting || !formData.client_name.trim()}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-2"></i>
                                                Update Client
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Client Modal */}
            {showDeleteModal && selectedClient && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle me-2 text-danger"></i>
                                    Delete Client
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeModals}
                                    disabled={submitting}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>
                                <p>
                                    Are you sure you want to delete the client <strong>"{selectedClient.client_name}"</strong>?
                                </p>
                                <p className="text-muted">
                                    Client ID: <code>{selectedClient.client_id}</code>
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeModals}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteClient}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-trash me-2"></i>
                                            Delete Client
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientAdmin;
